import React, { useEffect, useMemo } from "react";
import { connect } from "react-redux";
import { withRouter, RouteComponentProps } from "react-router-dom";
import NoContentMessage from "../../../common/NoContentMessage";
import BackButton from "../../../common/BackButton";
import ListGiftCards from "../../../common/ListGiftCards";
import styled from "styled-components";
import { GiftCardPurchaseOption } from "../../../../types/gift-card";
import { fetchPurchaseOptions } from "../../../../actions/creators/user/walletPurchase";
import { getWalletPurchaseOptions } from "../../../../reducers/user/wallet-cards";
import WalletBuyGiftInfo from "./WalletBuyGiftInfo";
import PaymentSidebar from "../../../checkout/PaymentSidebar";
import { CHECKOUT_CONTEXT_GIFT_CARD } from "../../../../actions/creators/checkout";
import MDSpinner from "react-md-spinner";
import BottomPanel from "../../../common/BottomPanel";
import WalletBuyItemDetail from "./WalletBuyItemDetail";
import WalletBuyAmountInput from "./WalletBuyAmountInput";
import { getGiftCardPurchasePaymentResult, isGiftCardPaymentProcessing } from "../../../../reducers";
import CardAnimation from "../../../checkout/CardAnimation";
import { WALLET_PURCHASE_RESULT_CLOSE } from "../../../../actions/types";
import { CARD_TYPE } from "../../../../types/card-type";
import WalletBuyConfirmation from "./WalletBuyConfirmation";
import { trackWalletPurchaseByTypeLoad, trackWalletPurchaseComplete, trackWalletPurchaseOptionSelect } from "../../../../utils/analytics/wallet";

const Container = styled.div`
    width: 100%;
`;

const STEPS = {
    SELECT_CARD: 1,
    CARD_DETAIL: 2,
    SELECT_DESIGN: 3,
};

interface Props {
    processing: boolean;
    purchaseOptions: GiftCardPurchaseOption[] | null;
    result: any | null;
    close: () => void;
    fetch: () => void;
}

const UserWalletBuyByType: React.FC<Props & RouteComponentProps> = ({ match, purchaseOptions, fetch, processing, result, close }) => {
    const [readyToPay, setReadyToPay] = React.useState<boolean>(false);
    const [selectedCard, setSelectedCard] = React.useState<GiftCardPurchaseOption | null>(null);
    const [selectedDesign, setSelectedDesign] = React.useState<GiftCardPurchaseOption | null>(null);
    const [isGift, setIsGift] = React.useState<boolean | undefined>(undefined);
    const [customAmount, setCustomAmount] = React.useState<number | null>(null);
    const [step, setStep] = React.useState<number>(1);
    const [giftInfo, setGiftInfo] = React.useState<{
        gift: string;
        giftFromName: string;
        giftToName: string;
        giftToEmail: string;
        giftToPhone: string;
        giftMessage: string;
    } | undefined>(undefined);
    const { type } = match.params;

    useEffect(() => {
        if (type && purchaseOptions) {
            trackWalletPurchaseByTypeLoad(type, purchaseOptions);
        }
    }, [type, purchaseOptions]);

    useEffect(() => {
        if (readyToPay && selectedDesign) {
            trackWalletPurchaseOptionSelect(selectedDesign, customAmount);
        }
    }, [readyToPay, selectedDesign, customAmount]);

    useEffect(() => {
        if (result && selectedDesign) {
            trackWalletPurchaseComplete(selectedDesign, customAmount, isGift === true, result);
        }
    }, [result, selectedDesign, customAmount, isGift]);

    useEffect(() => {
        if (null === purchaseOptions || !purchaseOptions.length) {
            fetch();
        }
    }, [purchaseOptions]);

    useEffect(() => {
        return close;
    }, [close]);

    useEffect(() => {
        setReadyToPay(selectedDesign && (false === isGift || giftInfo) && (!selectedDesign.isVariablePrice || customAmount) ? true : false);
    }, [isGift, giftInfo, selectedDesign, customAmount]);

    useEffect(() => {
        setSelectedDesign(null);
        setCustomAmount(null);
        if (selectedCard && selectedCard.designs && selectedCard.designs.length === 1) {
            setSelectedDesign(selectedCard.designs[0]);
        }
        if (!selectedCard) {
            setIsGift(undefined);
        }
    }, [selectedCard]);

    useEffect(() => {
        if (!selectedCard) {
            setStep(STEPS.SELECT_CARD);
            return;
        }

        if (isGift === undefined) {
            setStep(STEPS.CARD_DETAIL);
            return;
        }

        if (selectedCard.designs && selectedCard.designs.length > 1 && !selectedDesign) {
            setStep(STEPS.SELECT_DESIGN);
            return;
        }
    }, [selectedCard, isGift, selectedDesign]);

    const infoByType = {
        [CARD_TYPE.TICKETCARD]: {
            title: "TicketCard",
            description: "¡Para los amantes del cine! Elige la opción de boletos que mejor se adapte para ti o para esa persona especial a la que quieras regalarle una experiencia maravillosa",
        },
        [CARD_TYPE.CINEFAN]: {
            title: "Cinefan",
            description: "Con una tarjeta Cinefan accede a 20 boletos y 20 combos a un precio de descuento. ¡No te la pierdas!",
        },
        [CARD_TYPE.MOVIECARD]: {
            title: "MovieCard",
            description: "Compra una tarjeta MovieCard, recarga saldo o dale un increíble regalo a tus seres queridos. ¡Puedes utilizarla en taquilla y en medios digitales!",
        },
    };

    if (!(type in infoByType)) return null;

    if (null === purchaseOptions) {
        return <div className="p-5 text-center"><MDSpinner singleColor="var(--primary)" /></div>;
    }

    const options = purchaseOptions.filter(option => option.type === type);

    if (result) {
        return <WalletBuyConfirmation result={result} />;
    }

    return <>
        <Container>
            {STEPS.SELECT_CARD === step && renderCardsList({ info: infoByType[type], options, setSelectedCard })}
            {STEPS.CARD_DETAIL === step && renderSelectedCardDetail({ selectedCard, setIsGift, setSelectedCard, setCustomAmount })}
            {STEPS.SELECT_DESIGN === step && renderDesignSelect({ selectedCard, setSelectedDesign, setIsGift })}
            {selectedDesign && isGift && renderGiftInfo({ setGiftInfo, setSelectedDesign })}
            {readyToPay && renderPaymentSidebar({ selectedDesign, isGift, giftInfo, customAmount, onCancel: () => setReadyToPay(false) })}
        </Container>

        {processing && <CardAnimation />}
    </>;
};

function renderCardsList({ info, options, setSelectedCard }) {
    return <>
        <BackButton
            backTo="/usuario/wallet/comprar"
            label={info.title}
        />

        {!options.length ? (
            <NoContentMessage
                icon="fa-light fa-credit-card"
                title="No hay tarjetas disponibles"
                style={{ maxWidth: "37.5rem" }}
            >
                <p>Proximamente podrás adquirir tarjetas</p>
            </NoContentMessage>
        ) : <>
            <p className="my-5">{info.description}</p>

            {options.length && (
                <ListGiftCards
                    giftCards={options}
                    onSelectGiftCard={(card) => setSelectedCard(card as GiftCardPurchaseOption)}
                    className="my-5"
                />
            )}
        </>}
    </>;
}

function renderSelectedCardDetail({ selectedCard, setIsGift, setSelectedCard, setCustomAmount }) {
    if (!selectedCard) return null;

    return <WalletBuyItemDetail
        selectedCard={selectedCard}
        setCustomAmount={setCustomAmount}
        setIsGift={setIsGift}
        onClose={() => setSelectedCard(null)}
    />
}

function renderDesignSelect({ selectedCard, setSelectedDesign, setIsGift }) {
    return <BottomPanel title="Elige tu diseño" onClose={() => setIsGift(undefined)} desktopInline open>
        <ListGiftCards
            giftCards={selectedCard.designs}
            onSelectGiftCard={(card) => setSelectedDesign(card as GiftCardPurchaseOption)}
        />
    </BottomPanel>;
}

function renderGiftInfo({ setGiftInfo, setSelectedDesign }) {
    return <WalletBuyGiftInfo
        onClose={() => setSelectedDesign(null)}
        onSubmit={(data) => setGiftInfo(data)}
    />;
}

function renderPaymentSidebar({ selectedDesign, isGift, giftInfo, customAmount, onCancel }) {
    if (!selectedDesign) return null;

    const finalPrice = selectedDesign.isVariablePrice && customAmount ? customAmount : selectedDesign.price;

    return <PaymentSidebar
        context={CHECKOUT_CONTEXT_GIFT_CARD}
        cash={finalPrice}
        transactionData={{
            context: CHECKOUT_CONTEXT_GIFT_CARD,
            items: [{
                type: selectedDesign.type,
                id: selectedDesign.id,
                price: finalPrice
            }],
            gift: isGift && giftInfo ? giftInfo.gift : false,
            ...giftInfo
        }}
        ccRequired
        method="credit-card"
        paymentGateway="stripe-elements"
        termsChecked
        onCancel={onCancel}
    />;
}

const mapStateToProps = (state: any) => ({
    purchaseOptions: getWalletPurchaseOptions(state),
    processing: isGiftCardPaymentProcessing(state),
    result: getGiftCardPurchasePaymentResult(state),
});

const mapDispatchToProps = (dispatch: any) => ({
    close: () => dispatch({ type: WALLET_PURCHASE_RESULT_CLOSE }),
    fetch: () => dispatch(fetchPurchaseOptions()),
});


export default withRouter(
    connect(
        mapStateToProps,
        mapDispatchToProps
    )(UserWalletBuyByType)
);
